import { useMutation } from '@api/hooks'
import IconWithPopover from '@components/IconWithPopover/IconWithPopover'
import { Rebate as RebateType } from '@domain'
import { Paper, Stack, Switch, Typography } from '@mui/material'
import { MarketBasketsKeys } from '@services/marketBaskets.api'
import ProgramsApi, { ProgramsKeys } from '@services/programs.api'
import { useQueryClient } from '@tanstack/react-query'
import { numToCurrency } from '@utils/stringHelpers'
import { useMarketBasketContext } from '@views/MarketBaskets/MarketBaskets.hooks'
import { useParams } from 'react-router-dom'

import Property from './Property'

export default function Rebate({
	rebate,
	disableSelection,
}: {
	rebate: RebateType
	disableSelection: boolean
}) {
	// const { meetsCriteria, programId } = contract
	const { marketBasketId, similarProductId } = useParams()

	const queryClient = useQueryClient()

	// const allowSelection = isMatchedProduct && meetsCriteria && !program.selected

	const { mutate } = useMutation({
		mutationFn: ProgramsApi.selectProgram,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: MarketBasketsKeys.marketBasket(marketBasketId),
			})
			queryClient.invalidateQueries({
				queryKey: ProgramsKeys.basketPrograms(marketBasketId, similarProductId),
			})
		},
	})

	const handleSelect = () => {
		mutate({
			marketBasketId,
			similarProductId,
			programId: rebate.id,
			programType: 'rebate',
		})
	}

	return <RebateUi rebate={rebate} onSelect={handleSelect} disableSelection={disableSelection} />
}

export function RebateUi({
	rebate,
	onSelect,
	disableSelection,
}: {
	rebate: RebateType
	onSelect: () => void
	disableSelection: boolean
}) {
	const { marketBasket } = useMarketBasketContext()
	const currency = marketBasket?.currency || 'USD'

	const markup = `(${rebate.memberValue}) ${rebate.valueType}`
	const disqualified = !!rebate.disqualificationReason || disableSelection
	const disqualifiedReason = disableSelection
		? `Not a matched product.\n ${rebate.disqualificationReason || ''}`
		: rebate.disqualificationReason

	const selectButton = (
		<Stack direction="row" spacing={1}>
			{disqualified && <IconWithPopover>{disqualifiedReason}</IconWithPopover>}
			<Switch
				checked={rebate.selected}
				disabled={disqualified || disableSelection}
				onChange={onSelect}
			/>
		</Stack>
	)

	return (
		<Paper
			variant="outlined"
			sx={{
				padding: '1rem',
				width: '100%',
				color: disqualified ? 'text.disabled' : 'text.primary',
				borderColor: rebate.selected ? 'secondary.main' : 'gray[200]',
			}}
		>
			{rebate && (
				<Stack spacing={1}>
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Typography variant="h6" color={rebate.selected ? 'secondary.main' : 'text.primary'}>
							{rebate.termSetName}
						</Typography>
						{selectButton}
					</Stack>
					<Property label="Unit Savings" value={numToCurrency(rebate.unitSavings, currency)} />
					<Property label="Tier" value={rebate.tier === '' ? 'All' : rebate.tier} />
					<Property label="Contract ID" value={rebate.programId} />
					<Property label="Term Set ID" value={rebate.termSetId} />
					<Property label="Member Value" value={markup} />
				</Stack>
			)}
		</Paper>
	)
}
